import { useState, useEffect } from "react";
import { Bot, Shield, ShieldAlert, Flame, Play, Square, Clock, Loader2, Activity, Zap, Wallet, AlertTriangle, ShieldCheck, Grid3x3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BotStatusTracker } from "@/components/trading/BotStatusTracker";
import { DailyRiskStatus } from "@/components/trading/DailyRiskStatus";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { startBot, stopBot, fmtUsd, getRiskProfiles } from "@/lib/api";
import { toast } from "sonner";

const RISK_META = {
  low: { label: "Düşük Risk", icon: Shield, sl: "%5", color: "#00FFA3", desc: "Spot SL mesafesi %5; maksimum kayıp garantisi değildir." },
  medium: { label: "Orta Risk", icon: ShieldAlert, sl: "%15", color: "#A374FF", desc: "Spot SL mesafesi %15; kayma ve açık zarar mümkündür." },
  high: { label: "Yüksek Risk", icon: Flame, sl: "%30", color: "#FF3366", desc: "Spot SL mesafesi %30; büyük düşüş riski taşır." },
};
const DURATIONS = [
  { key: "1d", label: "1 Gün" }, { key: "3d", label: "3 Gün" }, { key: "5d", label: "5 Gün" },
  { key: "1w", label: "1 Hafta" }, { key: "2w", label: "2 Hafta" }, { key: "3w", label: "3 Hafta" },
  { key: "1m", label: "1 Ay" },
];
const CONFIRM_OPTS = [60, 90, 120];
const CRASH_OPTS = [3, 5, 8];

function fmtRemaining(sec) {
  if (sec == null) return "--";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}g ${h}s`;
  if (h > 0) return `${h}s ${m}dk`;
  return `${m}dk`;
}

const BotCard = ({ bot, onStop }) => {
  const meta = RISK_META[bot.risk_mode] || {};
  const isFutures = bot.market_type === "futures";
  const [rem, setRem] = useState(bot.remaining_seconds);
  useEffect(() => { setRem(bot.remaining_seconds); }, [bot.remaining_seconds]);
  useEffect(() => {
    const iv = setInterval(() => setRem((r) => (r != null && r > 0 ? r - 1 : r)), 1000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div data-testid={`bot-card-${bot.id}`} className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3.5">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-head font-bold text-sm" style={{ color: meta.color }}>{meta.label || bot.risk_mode}</span>
        {bot.live_trading && <span data-testid="bot-live-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FF3366]/15 text-[var(--neg)] border border-[#FF3366]/30">GERÇEK</span>}
        {bot.aggressive && <span data-testid="bot-aggressive-badge" className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[var(--warn)] border border-[#FFB020]/30"><Flame className="h-3 w-3" /> AGRESİF</span>}
        {bot.strategy === "grid" && <span data-testid="bot-grid-badge" className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#60A5FA] border border-[#3B82F6]/30"><Grid3x3 className="h-3 w-3" /> GRID</span>}
        {isFutures && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[var(--warn)]">x{bot.leverage}</span>}
        <span className="ml-auto flex items-center gap-1.5 text-[11px] font-mono" style={{ color: meta.color }}>
          <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: meta.color }} /> İZLENİYOR
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div><span className="text-[var(--text-faint)] block">Coinler</span><span className="font-mono text-[var(--text-primary)]">{(bot.symbols || []).join(", ") || "—"}</span></div>
        <div><span className="text-[var(--text-faint)] block">Sermaye</span><span className="font-mono text-[var(--pos)]">{fmtUsd(bot.capital)}</span></div>
        <div><span className="text-[var(--text-faint)] block flex items-center gap-1"><Clock className="h-3 w-3" />Kalan</span><span className="font-mono text-[var(--text-primary)]">{fmtRemaining(rem)}</span></div>
      </div>
      <div data-testid={`bot-risk-protection-${bot.id}`} className="text-[10px] text-[var(--text-faint)] mt-1.5 flex items-start gap-1">
        {bot.strategy === "grid" ? <AlertTriangle className="h-3 w-3 shrink-0 text-[var(--warn)]" /> : <ShieldCheck className="h-3 w-3 shrink-0" />}
        {bot.strategy === "grid"
          ? <span className="text-[var(--warn)]">Grid · en fazla {bot.grid_count ?? 10} kademe · otomatik zarar-kes yok</span>
          : bot.stop_loss_enabled === false
          ? <span className="text-[var(--neg)] font-bold">Stop-Loss KAPALI</span>
          : bot.market_type === "futures"
          ? <span>Vadeli SL: fiyat eşiği · ek onay süresi yok</span>
          : <>SL onay {bot.sl_confirm_seconds ?? 120}sn · sert çöküş %{Math.round((bot.sl_crash_margin ?? 0.05) * 100)}</>}
      </div>
      {bot.execution_warning && <p data-testid={`bot-execution-warning-${bot.id}`} role="alert"
        className="mt-2 text-xs text-[var(--warn)] break-words">{bot.execution_warning}</p>}
      <DailyRiskStatus bot={bot} />
      <BotStatusTracker bot={bot} />
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button data-testid={`bot-stop-${bot.id}`} size="sm" variant="outline"
            className="w-full mt-2.5 h-9 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
            <Square className="h-3.5 w-3.5 mr-1.5" /> Durdur
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head text-[var(--neg)]">Bu botu durdur?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-dim)]">
              Bu botun ({(bot.symbols || []).join(", ")}) takip edilen pozisyonları için <b className="text-[var(--text-primary)]">piyasa kapatma emri gönderilecek</b>. Dolum borsa koşullarına bağlıdır; diğer botların varlıkları satılmaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid={`bot-stop-confirm-${bot.id}`} onClick={() => onStop(bot.id)}
              className="bg-[var(--neg)] text-white font-bold hover:bg-[var(--neg-hover)]">Evet, Durdur ve Sat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const BotManagementPanel = ({ bots = [], events = [], onChanged, mode = "spot", coins = [], presetRisk, availableUsdt = 0, futuresUsdt = 0, gateConnected = false }) => {
  const [risk, setRisk] = useState(presetRisk || "medium");
  const [duration, setDuration] = useState("1w");
  const [selected, setSelected] = useState([]);
  const [leverage, setLeverage] = useState(1);
  const [policies, setPolicies] = useState(null);
  const [policyError, setPolicyError] = useState(false);
  const loadPolicies = async () => {
    try { const data = await getRiskProfiles(); setPolicies(Object.fromEntries(data.profiles.map((p) => [p.key, p]))); setPolicyError(false); }
    catch { setPolicyError(true); }
  };
  useEffect(() => { loadPolicies(); }, []);
  const policy = policies?.[risk];
  useEffect(() => { if (policy) setLeverage((v) => Math.min(v, policy.leverage_limit)); }, [policy]);
  const [capital, setCapital] = useState("");
  const [live, setLive] = useState(false);
  const [aggressive, setAggressive] = useState(false);
  const [confirmSec, setConfirmSec] = useState(120);
  const [crashPct, setCrashPct] = useState(5);
  const [slEnabled, setSlEnabled] = useState(true);
  const [strategy, setStrategy] = useState("smart");
  const [gridCount, setGridCount] = useState(10);
  const [gridStep, setGridStep] = useState(null); // null = auto (fee-safe min)
  const [busy, setBusy] = useState(false);
  const isFutures = mode === "futures";

  const effAvail = isFutures && live ? (futuresUsdt || 0) : (availableUsdt || 0);
  const capNum = parseFloat(capital || "0");
  const insufficient = capNum > 0 && capNum > effAvail + 1e-6;
  const capInvalid = !capital || capNum < 10 || insufficient;

  const toggleCoin = (sym) => setSelected((p) => (p.includes(sym) ? p.filter((s) => s !== sym) : [...p, sym]));

  const start = async () => {
    if (!policy || capInvalid) { toast.error(insufficient ? "Yetersiz Bakiye" : "Sermaye ve risk kurallarını kontrol edin."); return; }
    setBusy(true);
    try {
      const created = await startBot({ risk_mode: risk, duration_key: duration, symbols: selected, market_type: mode,
        leverage: isFutures ? leverage : 1, capital: capNum, live_trading: live,
        aggressive: !isFutures && strategy === "smart" && aggressive,
        strategy: isFutures ? "smart" : strategy,
        grid_count: (!isFutures && strategy === "grid") ? gridCount : undefined,
        grid_step_pct: (!isFutures && strategy === "grid" && gridStep) ? gridStep : undefined,
        stop_loss_enabled: slEnabled,
        sl_confirm_seconds: isFutures ? undefined : confirmSec,
        sl_crash_margin: isFutures ? undefined : crashPct / 100 });
      toast.success(`${!isFutures && live ? "GERÇEK" : (isFutures ? `Kaldıraçlı x${leverage}` : "Spot")} bot başlatıldı · ${fmtUsd(capNum)}`);
      setSelected([]); setCapital("");
      onChanged(created);
    } catch (e) { toast.error(e.response?.data?.detail || "Başlatılamadı"); }
    finally { setBusy(false); }
  };

  const doStop = async (botId) => {
    try { const result = await stopBot(botId); toast(result.execution_warning || "Yeni alımlar durduruldu. Kapatma sonucu etkinlik günlüğünde."); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Durdurulamadı"); }
  };

  const accent = isFutures ? "#FFB020" : "#00FFA3";
  const myBots = bots.filter((b) => (isFutures ? b.market_type === "futures" : b.market_type !== "futures"));

  return (
    <div data-testid={isFutures ? "futures-bot-panel" : "bot-management-panel"}
      className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 h-14 border-b border-[var(--border)]">
        <div className="h-8 w-8 rounded-md flex items-center justify-center border" style={{ background: `${accent}1A`, borderColor: `${accent}4D` }}>
          {isFutures ? <Zap className="h-4 w-4" style={{ color: accent }} /> : <Bot className="h-4 w-4" style={{ color: accent }} />}
        </div>
        <h3 className="font-head font-bold text-base">{isFutures ? "Kaldıraçlı Botlar (Futures)" : "Bot Yönetim Paneli"}</h3>
        {myBots.length > 0 && (
          <span data-testid="active-bots-count" className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full" style={{ background: `${accent}1A`, color: accent }}>
            {myBots.length} aktif bot
          </span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {myBots.length > 0 && (
          <div data-testid="active-bots-list" className="space-y-2.5">
            {myBots.map((b) => <BotCard key={b.id} bot={b} onStop={doStop} />)}
          </div>
        )}

        <div className="rounded-lg border border-dashed border-[var(--border)] p-4 space-y-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--text-dim)]">
            {myBots.length > 0 ? "+ Yeni Bot Ekle" : "Bot Başlat"}
          </div>

          <div>
            <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Coin Seçimi <span className="text-[var(--text-faint)] normal-case">(boş = kalan tüm coinler)</span></div>
            <div className="flex flex-wrap gap-2">
              {coins.map((c) => {
                const sel = selected.includes(c.symbol);
                return (
                  <button key={c.symbol} data-testid={`coin-select-${c.symbol}`} onClick={() => toggleCoin(c.symbol)}
                    className={cn("flex items-center gap-1.5 rounded-full border px-3 h-8 text-xs font-mono transition-all",
                      sel ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]")}>
                    {c.image && <img src={c.image} alt="" className="h-4 w-4 rounded-full" />}{c.symbol}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Risk Seviyesi</div>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(RISK_META).map(([key, m]) => {
                const Icon = m.icon; const sel = risk === key;
                return (
                  <button key={key} data-testid={`risk-${key}`} onClick={() => setRisk(key)}
                    className={cn("flex items-center gap-3 rounded-md border p-3 text-left transition-all", sel ? "bg-[var(--surface-hover)]" : "border-[var(--border)] hover:bg-[var(--surface-hover)]")}
                    style={sel ? { borderColor: m.color } : {}}>
                    <div className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${m.color}1A` }}><Icon className="h-4 w-4" style={{ color: m.color }} /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2"><span className="font-head font-bold text-sm" style={{ color: sel ? m.color : "#fff" }}>{m.label}</span>
                        <span data-testid={`risk-summary-${key}`} className="text-[10px] font-mono text-[var(--warn)]">{strategy === "grid" && !isFutures ? "Grid · SL yok" : `SL ${m.sl}`}</span></div>
                      <div className="text-[11px] text-[var(--text-dim)] leading-snug mt-0.5">{m.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {policy ? <p data-testid="new-bot-risk-policy" className="text-xs text-[var(--text-dim)] leading-relaxed border-l-2 border-[var(--warn)] pl-3">Yeni bot: günlük kayıp eşiği %{policy.daily_loss_pct}{isFutures ? ` · kaldıraç tavanı ${policy.leverage_limit}x` : ""}. Eşikte yeni girişler 00:00 UTC'ye kadar durur; koruyucu çıkışlar sürer. Mevcut botlar değişmez.</p> : <div data-testid="new-bot-risk-policy-loading" className="text-xs text-[var(--warn)]">{policyError ? "Risk kuralları alınamadı." : "Risk kuralları yükleniyor…"}{policyError && <Button data-testid="new-bot-risk-policy-retry" variant="outline" size="sm" onClick={loadPolicies}>Tekrar dene</Button>}</div>}

          {isFutures && (
            <div>
              <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2 flex items-center justify-between"><span>Kaldıraç</span><span className="font-mono text-[var(--warn)]">x{leverage}</span></div>
              <div className="grid grid-cols-3 gap-2">
                {[1,2,3].filter((lv) => lv <= (policy?.leverage_limit || 1)).map((lv) => (
                  <button key={lv} data-testid={`leverage-${lv}`} onClick={() => setLeverage(lv)}
                    className={cn("h-9 rounded-md border text-xs font-mono transition-all", leverage === lv ? "border-[var(--warn)] bg-[#FFB020]/10 text-[var(--warn)]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text-primary)]")}>x{lv}</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Çalışma Süresi</div>
            <div className="grid grid-cols-4 gap-2">
              {DURATIONS.map((d) => (
                <button key={d.key} data-testid={`duration-${d.key}`} onClick={() => setDuration(d.key)}
                  className={cn("h-10 rounded-md border text-xs font-mono transition-all", duration === d.key ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]")}>{d.label}</button>
              ))}
            </div>
          </div>

          {/* trading mode (spot + futures live) */}
          <div>
              <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Bot Modu</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="mode-paper" onClick={() => setLive(false)}
                  className={cn("h-11 rounded-md border text-xs font-bold transition-all", !live ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}>Simülasyon (Paper)</button>
                <button type="button" data-testid="mode-live" disabled={!gateConnected} onClick={() => gateConnected && setLive(true)}
                  className={cn("h-11 rounded-md border text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed", live ? "border-[var(--neg)] bg-[#FF3366]/10 text-[var(--neg)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}>Gerçek (Gate.io)</button>
              </div>
              {live ? (
                <p data-testid="live-warning" className="flex items-start gap-1.5 text-[11px] text-[#FFD98A] mt-2 rounded-md bg-[#FFB020]/10 border border-[#FFB020]/25 px-3 py-2">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> Bot GERÇEK PARA ile işlem yapar. {isFutures ? "Long/Short kaldıraçlı pozisyonlar" : "Tüm adımlar"} Gate.io'da gerçek emir olarak gönderilir.
                </p>
              ) : !gateConnected ? (<p className="text-[10px] text-[var(--text-faint)] mt-1.5">Gerçek işlem için önce API Ayarları'ndan Gate.io hesabınızı bağlayın.</p>) : null}
            </div>

          {!isFutures && (
            <div>
              <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Strateji</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="strategy-smart" onClick={() => setStrategy("smart")}
                  className={cn("h-11 rounded-md border text-xs font-bold transition-all px-2", strategy === "smart" ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}>Akıllı (Dip Alım + AI)</button>
                <button type="button" data-testid="strategy-grid" onClick={() => setStrategy("grid")}
                  className={cn("flex items-center justify-center gap-1.5 h-11 rounded-md border text-xs font-bold transition-all px-2", strategy === "grid" ? "border-[#3B82F6] bg-[#3B82F6]/10 text-[#60A5FA]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}><Grid3x3 className="h-3.5 w-3.5" /> Grid</button>
              </div>
              <p className="text-[10px] text-[var(--text-faint)] mt-1.5">{strategy === "grid" ? "Grid hedefleri ücret payı içerir. Gerçek sonuç dolum fiyatına bağlıdır; açık lotlar zarar edebilir." : "Akıllı: RSI/Bollinger + AI haber filtresiyle dipten kademeli alım."}</p>
            </div>
          )}

          {!isFutures && strategy === "grid" && (
            <div data-testid="grid-config" className="rounded-md border border-[#3B82F6]/30 bg-[#3B82F6]/5 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] text-[var(--text-dim)] uppercase tracking-wider flex items-center gap-1.5"><Grid3x3 className="h-3.5 w-3.5" /> Grid kademe sayısı</span>
                <span className="font-mono text-[#60A5FA] text-sm" data-testid="grid-count-value">{gridCount}</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[6, 10, 15, 20].map((n) => (
                  <button key={n} type="button" data-testid={`grid-count-${n}`} onClick={() => setGridCount(n)}
                    className={cn("h-9 rounded-md border text-xs font-mono transition-all", gridCount === n ? "border-[#3B82F6] bg-[#3B82F6]/10 text-[#60A5FA]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text-primary)]")}>{n}</button>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 mb-2">
                <span className="text-[11px] text-[var(--text-dim)] uppercase tracking-wider">Kademe adımı (kâr aralığı)</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[{v: null, l: "Oto"}, {v: 0.005, l: "%0.5"}, {v: 0.01, l: "%1"}, {v: 0.02, l: "%2"}].map((o) => (
                  <button key={o.l} type="button" data-testid={`grid-step-${o.v === null ? "auto" : o.l.replace("%","").replace(".","-")}`} onClick={() => setGridStep(o.v)}
                    className={cn("h-9 rounded-md border text-xs font-mono transition-all", gridStep === o.v ? "border-[#3B82F6] bg-[#3B82F6]/10 text-[#60A5FA]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text-primary)]")}>{o.l}</button>
                ))}
              </div>
              <p className="text-[10px] text-[var(--text-faint)] mt-2">Oto adım, ücret varsayımı ve güvenlik payına dayanır. Daha geniş adım daha seyrek işlem yaratabilir. Kayma, ücretler ve açık pozisyon zararları nedeniyle kâr garantisi yoktur.</p>
              <p data-testid="grid-risk-limitation-warning" role="alert" className="text-[11px] text-[var(--warn)] mt-2">Grid fiyat bazlı zarar-kes kullanmaz. Yeni botlarda risk seviyesine göre günlük giriş limiti uygulanır; açık lot zararları büyüyebilir.</p>
            </div>
          )}

          {!isFutures && strategy === "smart" && (
            <div>
              <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2">Giriş Stratejisi</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="entry-dip" onClick={() => setAggressive(false)}
                  className={cn("h-11 rounded-md border text-xs font-bold transition-all px-2", !aggressive ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}>Dip Alım (Güvenli)</button>
                <button type="button" data-testid="entry-aggressive" onClick={() => setAggressive(true)}
                  className={cn("flex items-center justify-center gap-1.5 h-11 rounded-md border text-xs font-bold transition-all px-2", aggressive ? "border-[var(--warn)] bg-[#FFB020]/10 text-[var(--warn)]" : "border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]")}><Flame className="h-3.5 w-3.5" /> Agresif</button>
              </div>
              <p className="text-[10px] text-[var(--text-faint)] mt-1.5">{aggressive ? "Agresif: RSI uygun olduğunda ilk kademe canlı fiyattan hemen alınır." : "Dip Alım: fiyat teknik destek seviyesine düşünce parça parça alır."}</p>
            </div>
          )}

          {(isFutures || strategy === "smart") && (
          <div className="rounded-md border border-[var(--border)] p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-dim)] uppercase tracking-wider"><ShieldCheck className="h-3.5 w-3.5" /> Zarar Durdur (Stop-Loss)</div>
              <div className="flex gap-1.5">
                <button type="button" data-testid="sl-enabled-on" onClick={() => setSlEnabled(true)}
                  className={cn("h-8 px-3 rounded-md border text-[11px] font-bold transition-all", slEnabled ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)]")}>Açık</button>
                <button type="button" data-testid="sl-enabled-off" onClick={() => setSlEnabled(false)}
                  className={cn("h-8 px-3 rounded-md border text-[11px] font-bold transition-all", !slEnabled ? "border-[var(--neg)] bg-[#FF3366]/10 text-[var(--neg)]" : "border-[var(--border)] text-[var(--text-dim)]")}>Kapalı</button>
              </div>
            </div>
            {!slEnabled && (
              <p data-testid="sl-off-warning" className="flex items-start gap-1.5 text-[11px] text-[#FFD98A] mt-2 rounded-md bg-[#FFB020]/10 border border-[#FFB020]/25 px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> Stop-Loss KAPALI — bot zarar durdur satışı yapmaz. {isFutures ? "Borsa likidasyonu hâlâ mümkündür." : "Haber kaynaklı risk çıkışı ve manuel durdurma yine satış yapabilir."} Zarar riski yüksektir.
              </p>
            )}
          </div>
          )}

          {!isFutures && strategy === "smart" && slEnabled && (
            <div className="rounded-md border border-[var(--border)] p-3">
              <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Panik-Satış Koruması</div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] text-[var(--text-dim)]">SL onay süresi</span>
                <div className="flex gap-1.5">
                  {CONFIRM_OPTS.map((s) => (
                    <button key={s} type="button" data-testid={`sl-confirm-${s}`} onClick={() => setConfirmSec(s)}
                      className={cn("h-8 px-2.5 rounded-md border text-[11px] font-mono transition-all", confirmSec === s ? "border-[var(--pos)] bg-[#00FFA3]/10 text-[var(--pos)]" : "border-[var(--border)] text-[var(--text-dim)]")}>{s}sn</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-[var(--text-dim)]">Sert çöküş eşiği</span>
                <div className="flex gap-1.5">
                  {CRASH_OPTS.map((c) => (
                    <button key={c} type="button" data-testid={`sl-crash-${c}`} onClick={() => setCrashPct(c)}
                      className={cn("h-8 px-2.5 rounded-md border text-[11px] font-mono transition-all", crashPct === c ? "border-[var(--warn)] bg-[#FFB020]/10 text-[var(--warn)]" : "border-[var(--border)] text-[var(--text-dim)]")}>%{c}</button>
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-[var(--text-faint)] mt-2">Fiyat SL altına inince {confirmSec} sn beklenir (geçici dipte satmaz); SL'nin %{crashPct} altına hızla çökerse anında satar.</p>
            </div>
          )}

          <div>
            <div className="text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> {isFutures && live ? "Teminat (Vadeli USDT)" : "Sermaye (USDT)"}</span>
              <span className="font-mono text-[var(--pos)]" data-testid="available-usdt">{isFutures && live ? "Vadeli cüzdan: " : "Kullanılabilir: "}{fmtUsd(effAvail)}</span>
            </div>
            <Input data-testid="bot-capital" type="number" min="10" value={capital} onChange={(e) => setCapital(e.target.value)} placeholder="Örn: 1000"
              className={cn("bg-[var(--surface)] border-[var(--border)] font-mono h-11", insufficient && "border-[var(--neg)]")} />
            <div className="flex flex-wrap gap-2 mt-2">
              {[25, 50, 100].map((pct) => (
                <button key={pct} type="button" data-testid={`capital-pct-${pct}`} onClick={() => setCapital(String(Math.floor((effAvail || 0) * pct / 100)))}
                  className="rounded-full border border-[var(--border)] px-3 h-7 text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">%{pct}</button>
              ))}
            </div>
            {insufficient ? (
              <p data-testid="insufficient-balance" className="flex items-center gap-1.5 text-[11px] text-[var(--neg)] mt-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Yetersiz Bakiye.</p>
            ) : (<p className="text-[10px] text-[var(--text-faint)] mt-1.5">Her bot yalnızca kendi sermayesiyle işlem yapar; diğer botların/kasanın kalanına dokunmaz.</p>)}
          </div>

          <Button data-testid="bot-start-btn" onClick={start} disabled={busy || capInvalid || !policy}
            className={cn("w-full h-12 font-bold hover:opacity-90 disabled:opacity-40", !isFutures && live ? "text-white" : "text-black")}
            style={{ background: !isFutures && live ? "var(--neg)" : accent }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1.5" /> {!isFutures && live ? "GERÇEK Botu Başlat" : (isFutures ? "Kaldıraçlı Botu Başlat" : "Botu Başlat")}</>}
          </Button>
        </div>

        <div className="pt-4 border-t border-[var(--surface-hover)]">
          <div className="flex items-center gap-1.5 text-xs text-[var(--text-dim)] uppercase tracking-wider mb-2"><Activity className="h-3.5 w-3.5" /> Bot Etkinlik Günlüğü</div>
          <div data-testid="bot-events" className="space-y-1.5 max-h-[220px] overflow-y-auto">
            {(!events || events.length === 0) && <p className="text-[11px] text-[var(--text-faint)] py-2">Henüz etkinlik yok.</p>}
            {(events || []).map((e, i) => (
              <div key={i} className="flex gap-2 text-[12px] leading-snug">
                <span className="flex-shrink-0">•</span>
                <div className="min-w-0">
                  <span className="text-[var(--text-muted)]">{e.message}</span>
                  <span className="block text-[10px] text-[var(--text-faint)] font-mono">{new Date(e.time).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
